import classNames from 'classnames';
import React, { useState, useEffect, useRef } from 'react';

export interface TOCItem {
  href: string;
  title: React.ReactNode;
  key: string;
  alt?: string
  children?: TOCItem[];
}

interface TOCProps {
  items: TOCItem[];
}

const TOC: React.FC<TOCProps> = ({ items }) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement>>({});
  const hrefToKeyMap = useRef<Record<string, string>>({});

  // Helper function to get all hrefs from nested items
  const getAllHrefs = (items: TOCItem[]): string[] => {
    return items.flatMap(item => [
      item.href,
      ...(item.children ? getAllHrefs(item.children) : [])
    ]);
  };

  // Build href to key mapping
  useEffect(() => {
    const buildMap = (items: TOCItem[]) => {
      items.forEach(item => {
        hrefToKeyMap.current[item.href] = item.key;
        if (item.children) buildMap(item.children);
      });
    };

    buildMap(items);
    return () => {
      hrefToKeyMap.current = {};
      sectionRefs.current = {};
    };
  }, [items]);

  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const href = `#${entry.target.id}`;
          const key = hrefToKeyMap.current[href];
          if (key) setActiveKey(key);
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '0px 0px -90% 0px',
      threshold: 0.1
    });

    // Observe all sections
    getAllHrefs(items).forEach(href => {
      const id = href.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        sectionRefs.current[id] = element;
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [items]);

  const handleLinkClick = (e: React.MouseEvent, href: string, key: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveKey(key)
  };

  const renderItems = (items: TOCItem[], level = 0) => {
    return (
      <ul className={classNames(`${level > 0 ? 'mt-0.5' : ''}`, 'list-none', 'pl-0', 'ml-0')}>
        {items.map((item) => (
          <li key={item.key} className="ml-0 pl-0 w-full">
            <a
              href={item.href}
              onClick={(e) => handleLinkClick(e, item.href, item.key)}
              className={`py-0.5 px-0.5 transition-all overflow-hidden overflow-ellipsis whitespace-nowrap duration-200 border-l-2 border-l-transparent block  ${activeKey === item.key
                ? 'bg-blue-50 text-blue-600 font-medium border-l-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              style={{
                paddingLeft: `${1 + level}rem`,
                // marginLeft: level > 0 ? '0.25rem' : '0'
              }}
              title={item.alt ?? (typeof item.title === 'string' ? item.title : undefined)}
            >
              {item.title}
            </a>
            {item.children && (
              <div className="ml-0">
                {renderItems(item.children, level + 1)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return renderItems(items)
};

export function FloatingTOC(props: TOCProps) {
  return <div className="fixed top-7 max-h-[calc(100vh-20rem)] overflow-y-auto 4xl:w-18 left-5 max-4xl:left-0.5 3xl:w-15 max-3xl:hidden border border-light-grey p-1 rounded-lg">
      <div className="font-semibold text-lg">Содержание</div>
      <TOC {...props} />
  </div>
}


export default TOC;
